import { useState } from 'react';
import {
  LogOut,
  MapPin,
  MapPinOff,
  Moon,
  Sun,
  TriangleAlert,
  Wallet,
} from 'lucide-react';

import { displayName, isOnShift, vehicleLabel, type RiderProfile } from '@/api/types';
import { useAuth } from '@/auth/auth-context';
import { AlertsCard } from '@/components/alerts-card';
import { Main, PageHeader } from '@/components/app-shell';
import { ShiftSwitch } from '@/components/shift-switch';
import { Button } from '@/components/ui/button';
import { Card, CardBody, CardDivider } from '@/components/ui/card';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/lib/toast';
import { useRunSheet } from '@/hooks/use-deliveries';
import { useCashBalance } from '@/hooks/use-rider';
import { useRiderSession } from '@/auth/rider-session';
import type { LocationPing } from '@/hooks/use-location-ping';
import { formatMoney, formatTime, initials, isZeroMoney, plural } from '@/lib/format';
import { shiftDetail, shiftLabel } from '@/lib/status';
import { applyTheme, currentTheme } from '@/lib/theme';
import { cn } from '@/lib/utils';

/**
 * Who you are, whether you are working, and the way out.
 *
 * Short on purpose. The whole app is one list of jobs; this page exists for
 * the things that are true BETWEEN jobs — the shift, whether dispatch can
 * actually see where you are, whether new jobs reach you, what cash you are
 * holding, and signing off.
 */
export function AccountScreen({ rider }: { rider: RiderProfile }) {
  const { user, signOut } = useAuth();
  const { isCarrying } = useRunSheet();
  const { ping } = useRiderSession();
  const cash = useCashBalance();

  const [confirmingSignOut, setConfirmingSignOut] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [dark, setDark] = useState(() => currentTheme() === 'dark');

  return (
    <>
      <PageHeader title="You" />
      <Main className="grid gap-6 pt-5">
      <header className="flex items-center gap-4">
        <span className="grid size-14 shrink-0 place-items-center rounded-full bg-wash text-lg font-bold text-brand">
          {initials(rider.name ?? displayName(user))}
        </span>
        <div className="min-w-0">
          <p className="truncate text-xl font-bold tracking-tight text-ink">
            {rider.name ?? displayName(user)}
          </p>
          <p className="truncate text-sm text-ink-muted">
            {[user?.phone, vehicleLabel(rider)].filter(Boolean).join(' · ')}
          </p>
        </div>
      </header>

      <section aria-labelledby="shift-heading">
        <h2 id="shift-heading" className="eyebrow pb-2">
          Shift
        </h2>
        <Card>
          <CardBody className="flex items-center gap-4">
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-ink">{shiftLabel(rider)}</p>
              <p className="mt-0.5 text-sm leading-snug text-ink-muted">{shiftDetail(rider)}</p>
            </div>
            {/* The same widget as the one in the run sheet's bar. */}
            <ShiftSwitch rider={rider} />
          </CardBody>

          {rider.activeDeliveries > 0 ? (
            <>
              <CardDivider />
              <CardBody className="py-3">
                <p data-numeric className="text-sm text-ink-muted">
                  {plural(rider.activeDeliveries, 'job')} still open. Going off shift will not
                  clear them.
                </p>
              </CardBody>
            </>
          ) : null}
        </Card>
      </section>

      <section aria-labelledby="location-heading">
        <h2 id="location-heading" className="eyebrow pb-2">
          Location
        </h2>
        <LocationCard ping={ping} onShift={isOnShift(rider)} />
      </section>

      <section aria-labelledby="alerts-heading">
        <h2 id="alerts-heading" className="eyebrow pb-2">
          Alerts
        </h2>
        <AlertsCard compact />
      </section>

      <section aria-labelledby="cash-heading">
        <h2 id="cash-heading" className="eyebrow pb-2">
          Cash you are holding
        </h2>
        <Card variant={cash.data && !isZeroMoney(cash.data.outstanding) ? 'warning' : 'default'}>
          <CardBody className="flex items-start gap-3">
            <Wallet
              className={cn(
                'mt-0.5 size-5 shrink-0',
                cash.data && !isZeroMoney(cash.data.outstanding)
                  ? 'text-warning-ink'
                  : 'text-ink-subtle',
              )}
              aria-hidden="true"
            />
            <div className="min-w-0 flex-1">
              {cash.isPending ? (
                <p className="text-sm text-ink-muted">Checking with the office…</p>
              ) : cash.isError ? (
                <>
                  <p className="font-semibold text-ink">Could not reach the ledger</p>
                  <p className="mt-0.5 text-sm leading-snug text-ink-muted">
                    Every COD collection is still recorded against you. Settle up at the office.
                  </p>
                </>
              ) : (
                <>
                  <p data-numeric className="text-2xl font-bold tracking-tight text-ink">
                    {formatMoney(cash.data?.outstanding)}
                  </p>
                  <p className="mt-1 text-sm leading-snug text-ink-muted">
                    {cash.data && cash.data.entries > 0
                      ? `From ${plural(cash.data.entries, 'cash order')} since your last handover. Settle up at the office.`
                      : 'Nothing outstanding. Every COD collection is recorded here as you make it.'}
                  </p>
                </>
              )}
            </div>
          </CardBody>
        </Card>
      </section>

      <section aria-labelledby="appearance-heading">
        <h2 id="appearance-heading" className="eyebrow pb-2">
          Appearance
        </h2>
        <Card>
          <CardBody className="flex items-center gap-4">
            <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-muted text-ink-muted">
              {dark ? (
                <Moon className="size-5" aria-hidden="true" />
              ) : (
                <Sun className="size-5" aria-hidden="true" />
              )}
            </span>
            <div className="min-w-0 flex-1">
              <p id="dark-label" className="font-semibold text-ink">
                Dark screen
              </p>
              <p className="mt-0.5 text-sm leading-snug text-ink-muted">
                Easier on the eyes at a doorway after dark.
              </p>
            </div>
            <Switch
              checked={dark}
              aria-labelledby="dark-label"
              onCheckedChange={(next) => {
                setDark(next);
                applyTheme(next ? 'dark' : 'light');
              }}
            />
          </CardBody>
        </Card>
      </section>

      <Button
        variant="secondary"
        size="lg"
        block
        className="mt-2"
        onClick={() => {
          // Refused rather than warned about: signing out takes the rider off
          // shift, and a bag that leaves the board while somebody is still
          // carrying it is an order nobody is looking for.
          if (isCarrying) {
            toast.error('Finish or hand back the order you are carrying first.');
            return;
          }
          setConfirmingSignOut(true);
        }}
      >
        <LogOut aria-hidden="true" />
        Sign out
      </Button>

      <Sheet open={confirmingSignOut} onOpenChange={setConfirmingSignOut}>
        <SheetContent
          title="Sign out?"
          description="You will be taken off shift and will stop receiving jobs."
        >
          <div className="grid gap-2 pb-2">
            <Button
              variant="danger"
              size="lg"
              block
              loading={signingOut}
              loadingLabel="Signing out"
              onClick={async () => {
                setSigningOut(true);
                await signOut();
                setSigningOut(false);
              }}
            >
              Sign out
            </Button>
            <Button variant="ghost" size="md" block onClick={() => setConfirmingSignOut(false)}>
              Stay signed in
            </Button>
          </div>
        </SheetContent>
      </Sheet>
      </Main>
    </>
  );
}

/**
 * Whether dispatch can actually see this phone.
 *
 * Surfaced because it is otherwise invisible: a rider whose location
 * permission was never granted looks exactly like one who granted it, and
 * would spend a whole shift wondering why the near jobs go to somebody else.
 *
 * It also says plainly what a WEB app can and cannot promise. The stream stops
 * when the tab is hidden, which for a rider who switches to Maps for the whole
 * leg is most of it — claiming live tracking here would be claiming something
 * the browser does not offer.
 */
function LocationCard({ ping, onShift }: { ping: LocationPing; onShift: boolean }) {
  if (!onShift) {
    return (
      <Card variant="sunken">
        <CardBody className="flex items-start gap-3">
          <MapPinOff className="mt-0.5 size-5 shrink-0 text-ink-subtle" aria-hidden="true" />
          <div className="min-w-0">
            <p className="font-semibold text-ink">Not reporting</p>
            <p className="mt-0.5 text-sm leading-snug text-ink-muted">
              Your location is only sent while you are on shift. That is the whole point of the
              switch.
            </p>
          </div>
        </CardBody>
      </Card>
    );
  }

  const blocked = ping.status === 'blocked' || ping.status === 'unsupported';

  return (
    <Card variant={blocked ? 'warning' : 'default'}>
      <CardBody className="flex items-start gap-3">
        {blocked ? (
          <TriangleAlert className="mt-0.5 size-5 shrink-0 text-warning-ink" aria-hidden="true" />
        ) : (
          <MapPin className="mt-0.5 size-5 shrink-0 text-brand" aria-hidden="true" />
        )}
        <div className="min-w-0">
          <p className={cn('font-semibold', blocked ? 'text-warning-ink' : 'text-ink')}>
            {blocked ? 'The office cannot see where you are' : 'The office can see where you are'}
          </p>
          <p className="mt-0.5 text-sm leading-snug text-ink-muted">
            {ping.message ??
              (ping.at
                ? `Last sent at ${formatTime(ping.at)}. It pauses when you switch to another app, and catches up when you come back.`
                : 'Sending your position every twenty seconds while this screen is open.')}
          </p>
        </div>
      </CardBody>
    </Card>
  );
}
